import React, { useState } from "react";
import { SideNavigation, StudentNavbar, UserDashboardHeader } from "../components";
import { API_BASE_URL } from "../config";
import {
  groupedSchools,
  isCustomSchoolValue,
  encodeCustomSchool,
  decodeSchool,
} from "../constants/schools";

import student from "../assets/student.png";
import Alert from "@mui/material/Alert";
import axios from "axios";

// One field system for the whole form, so a name box, a school picker and the
// unchangeable email are visibly the same kind of thing. Read-only is a flat grey
// surface with no focus ring — the old outlined inputs lit up blue even when they
// could not be edited, which read as broken rather than locked.
const fieldClass = (editable) =>
  [
    // m-0 is load-bearing: App.css sets a global `input { margin: 20px auto }`, which
    // silently padded every text field while leaving the school <select> alone.
    "m-0 w-full min-h-[3.25rem] rounded-xl border px-4 py-3.5 text-sm text-slate-900 outline-none transition",
    "placeholder:text-slate-400",
    // A field has to look like a field before it is focused. slate-200 with a hairline
    // shadow was too close in value to the white section to read as an edge, so the box
    // disappeared into the page.
    "border-slate-300 shadow-[0_2px_6px_-1px_rgba(15,23,42,0.20),0_1px_2px_-1px_rgba(15,23,42,0.14)]",
    editable
      // A thin dark border plus a barely-there halo. The old 4px teal ring read as a
      // glowing blue box around the field, which looked like an error state.
      ? "bg-white focus:border-[#1c394a] focus:ring-2 focus:ring-[#1c394a]/10"
      : "cursor-default bg-slate-50 text-slate-500",
  ].join(" ");

// Inlined so the select keeps its own chevron once appearance is stripped.
const CHEVRON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%2394a3b8'%3E%3Cpath fill-rule='evenodd' d='M5.2 7.4a.75.75 0 0 1 1.1 0L10 11.2l3.7-3.8a.75.75 0 1 1 1.1 1l-4.25 4.35a.75.75 0 0 1-1.1 0L5.2 8.45a.75.75 0 0 1 0-1.05z' clip-rule='evenodd'/%3E%3C/svg%3E\")";

// Fields carry their name as a placeholder and an aria-label rather than a heading
// above the box: the section title already says what the group is, and a stack of
// small labels over pre-filled values is noise. Screen readers still get the name.
const Field = ({ hint, children }) => (
  <div className="min-w-0">
    {children}
    {hint && <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{hint}</p>}
  </div>
);

// A titled group of fields. The heading sits directly above what it describes, so the
// form reads top to bottom at every width instead of splitting into columns.
const Section = ({ title, children }) => (
  <div className="px-4 py-5 sm:px-6 lg:px-8">
    <h2 className="mb-2 text-sm font-semibold text-slate-900">{title}</h2>
    <div className="min-w-0">{children}</div>
  </div>
);

const initialsFor = (first, last) =>
  [first, last]
    .map((part) => String(part ?? "").trim()[0])
    .filter(Boolean)
    .join("")
    .toUpperCase() || "?";

const formatDateYYYYMMDD = (input) => {
  if (!input) return "—";
  const t = Date.parse(input);
  if (Number.isNaN(t)) return "—";
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const Profile = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [readOnly, setReadOnly] = useState(true);
  const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);

  const [changingPassword, setChangingPassword] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isPasswordSuccess, setIsPasswordSuccess] = useState(false);

  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const updatedAt = formatDateYYYYMMDD(storedUser?.updated_at || storedUser?.created_at);

  // The stored school is either a dropdown value or a typed-in name with its kind
  // appended. Split it so the select and the text box can each hold their own half.
  const storedSchool = decodeSchool(storedUser.school);
  const initials = initialsFor(storedUser.first_name, storedUser.last_name);

  // Where the form starts, and where Cancel puts it back.
  const savedProfile = () => ({
    firstName: storedUser.first_name,
    lastName: storedUser.last_name,
    email: storedUser.email,
    school: storedSchool.optionValue,
    customSchoolName: storedSchool.customName,
  });

  const [updatedUserInfo, setUpdatedUserInfo] = useState(savedProfile);

  const needsCustomSchoolName = isCustomSchoolValue(updatedUserInfo.school);

  const handleEditClick = () => setReadOnly(false);

  const handleCancelClick = () => {
    setReadOnly(true);
    setUpdatedUserInfo(savedProfile());
  };

  const handleSaveClick = () => {
    setReadOnly(true);

    axios
      .post(`${API_BASE_URL}/api/update/student`, {
        firstName: updatedUserInfo.firstName,
        lastName: updatedUserInfo.lastName,
        school: needsCustomSchoolName
          ? encodeCustomSchool(updatedUserInfo.customSchoolName, updatedUserInfo.school)
          : updatedUserInfo.school,
      })
      .then((response) => {
        const data = response.data;
        if (data.error) {
          console.log("User could not be updated");
        } else {
          localStorage.setItem("user", JSON.stringify(response.data.user));
          setIsSaveSuccessful(true);
          setTimeout(() => window.location.reload(), 2000);
        }
      });
  };

  const openPasswordForm = () => setChangingPassword(true);

  const closePasswordForm = () => {
    setChangingPassword(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
  };

  const passwordIncomplete = !currentPassword || !newPassword || !confirmPassword;

  const handlePasswordSave = () => {
    axios
      .post(`${API_BASE_URL}/api/update/password/student`, {
        currentPassword,
        newPassword,
        confirmPassword,
      })
      .then((response) => {
        const data = response.data;
        if (data.message) {
          setIsPasswordSuccess(true);
          setTimeout(() => window.location.reload(), 2000);
        }
      })
      .catch((error) => {
        setPasswordError(error?.response?.data?.error || "Password update failed.");
      });
  };

  return (
    <div className="min-h-screen w-full">
      {/* Fixed navbar + spacing below */}
      <StudentNavbar className="fixed top-0 w-full z-50" />

      <div>
        <UserDashboardHeader />

        <div className="px-3 sm:px-6 py-6">

          {/* Desktop grid layout */}
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
            {/* Sidebar (desktop) */}
            <aside className="hidden lg:block">
              <SideNavigation />
            </aside>

            {/* Main content */}
            <main className="w-full lg:pl-6">
              <div className="flex items-center justify-between gap-3">
                {/* Mobile menu button */}
                <div className="lg:hidden mb-4 w-full">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(true)}
                    className="inline-flex w-full items-center gap-2 rounded-lg bg-white/10 border border-white/10 px-4 py-2 text-sm text-white hover:bg-white/15 transition"

                  >
                    <span className="text-lg">☰</span>
                    Menu
                  </button>

                </div>
              </div>

              <div className="pt-3">
                {isSaveSuccessful && (
                  <Alert severity="success">User update was successful.</Alert>
                )}
              </div>

              {/* ── Profile card ───────────────────────────────────────────
                  Laid out as settings sections: each one says what it is for on the
                  left and holds its fields on the right, so a student can find the
                  thing they came to change instead of scanning a wall of boxes. */}
              <div className="overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-black/5">
                <div className="relative overflow-hidden bg-[#1c394a] px-4 py-6 sm:px-6 sm:py-7 lg:px-8">
                  <div className="pointer-events-none absolute -right-16 -top-24 h-56 w-56 rounded-full bg-[#2e92b0]/20 blur-3xl" />

                  <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-xl font-medium text-white ring-1 ring-white/20">
                      {initials}
                    </div>

                    <div className="min-w-0">
                      <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#7fd4ea]">
                        My Profile
                      </p>
                      <h1 className="mt-1 truncate text-2xl font-light text-white sm:text-3xl">
                        {storedUser.first_name} {storedUser.last_name}
                      </h1>
                      <p className="mt-1 truncate text-sm text-white/50">
                        {updatedUserInfo.email}
                      </p>
                    </div>

                    <div className="sm:ml-auto">
                      <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs text-white/70 ring-1 ring-white/10">
                        Updated
                        <span className="font-semibold text-white tabular-nums">{updatedAt}</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="divide-y divide-slate-100">
                  <Section title="Personal Details">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field>
                        <input
                          type="text"
                          aria-label="First name"
                          placeholder="First name"
                          value={updatedUserInfo.firstName || ""}
                          readOnly={readOnly}
                          onChange={(e) =>
                            setUpdatedUserInfo((prev) => ({ ...prev, firstName: e.target.value }))
                          }
                          className={fieldClass(!readOnly)}
                        />
                      </Field>

                      <Field>
                        <input
                          type="text"
                          aria-label="Last name"
                          placeholder="Last name"
                          value={updatedUserInfo.lastName || ""}
                          readOnly={readOnly}
                          onChange={(e) =>
                            setUpdatedUserInfo((prev) => ({ ...prev, lastName: e.target.value }))
                          }
                          className={fieldClass(!readOnly)}
                        />
                      </Field>
                    </div>
                  </Section>

                  <Section title="School">
                    <Field>
                      {/* A native select: it keeps the island groupings and gives phones
                          their own picker, which beats a long scrolling menu on a small
                          screen. */}
                      <select
                        aria-label="Your school"
                        value={updatedUserInfo.school || ""}
                        disabled={readOnly}
                        onChange={(e) =>
                          setUpdatedUserInfo((prev) => ({
                            ...prev,
                            school: e.target.value,
                            // Switching between homeschool and unlisted keeps what was
                            // typed; picking a real school clears it.
                            customSchoolName: isCustomSchoolValue(e.target.value)
                              ? prev.customSchoolName
                              : "",
                          }))
                        }
                        className={`${fieldClass(!readOnly)} appearance-none bg-[length:18px] bg-[right_1rem_center] bg-no-repeat pr-11`}
                        style={{ backgroundImage: CHEVRON }}
                      >
                        <option value="">Select a school</option>
                        {groupedSchools.map((group) => (
                          <optgroup key={group.label} label={group.label}>
                            {group.options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </Field>

                    {needsCustomSchoolName && (
                      <div className="mt-4">
                        <Field>
                          <input
                            type="text"
                            value={updatedUserInfo.customSchoolName}
                            readOnly={readOnly}
                            placeholder={
                              updatedUserInfo.school === "homeschool"
                                ? "Enter your homeschool name"
                                : "Enter your school name"
                            }
                            onChange={(e) =>
                              setUpdatedUserInfo((prev) => ({
                                ...prev,
                                customSchoolName: e.target.value,
                              }))
                            }
                            className={fieldClass(!readOnly)}
                          />
                        </Field>
                      </div>
                    )}
                  </Section>

                  <Section title="Sign-In & Security">
                    <Field hint="To update your email, contact info.edubahamas@gmail.com">
                      <input
                        type="email"
                        aria-label="Email"
                        value={updatedUserInfo.email || ""}
                        readOnly
                        className={fieldClass(false)}
                      />
                    </Field>

                    {/* The password form opens in place rather than over the page.
                        A modal for three fields hid the rest of the account behind a
                        backdrop for no benefit; expanding the row it belongs to keeps
                        the student where they are. */}
                    <div className="mt-5 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-800">Password</p>
                          <p className="mt-0.5 text-xs text-slate-500">
                            {changingPassword
                              ? "Enter your current password, then choose a new one."
                              : "Change it any time. You will need your current one."}
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={changingPassword ? closePasswordForm : openPasswordForm}
                          className={
                            changingPassword
                              ? "w-full shrink-0 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100 sm:w-auto"
                              : "w-full shrink-0 rounded-lg bg-[#1c394a] px-4 py-2.5 text-sm font-medium text-white transition hover:bg-[#22475d] sm:w-auto"
                          }
                        >
                          {changingPassword ? "Cancel" : "Change password"}
                        </button>
                      </div>

                      {changingPassword && (
                        <div className="border-t border-slate-200 p-4">
                          {passwordError && (
                            <div className="mb-4">
                              <Alert severity="error">{passwordError}</Alert>
                            </div>
                          )}
                          {isPasswordSuccess && (
                            <div className="mb-4">
                              <Alert severity="success">Password successfully updated!</Alert>
                            </div>
                          )}

                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                              <Field>
                                <input
                                  type="password"
                                  aria-label="Current password"
                                  placeholder="Current password"
                                  autoComplete="current-password"
                                  value={currentPassword}
                                  onChange={(e) => setCurrentPassword(e.target.value)}
                                  className={fieldClass(true)}
                                />
                              </Field>
                            </div>

                            <Field hint="At least 8 characters.">
                              <input
                                type="password"
                                aria-label="New password"
                                placeholder="New password"
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className={fieldClass(true)}
                              />
                            </Field>

                            <Field>
                              <input
                                type="password"
                                aria-label="Confirm new password"
                                placeholder="Confirm new password"
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className={fieldClass(true)}
                              />
                            </Field>
                          </div>

                          <div className="mt-5 flex justify-end">
                            <button
                              type="button"
                              onClick={handlePasswordSave}
                              disabled={passwordIncomplete}
                              className="w-full rounded-xl bg-[#1c394a] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#22475d] disabled:cursor-not-allowed disabled:bg-slate-300 sm:w-auto sm:py-2.5"
                            >
                              Update password
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </Section>
                </div>

                {/* Which actions exist follows the mode: one way in, two ways out.
                    Cancel puts every field back rather than leaving half-typed edits
                    sitting on screen. */}
                <div className="flex flex-col-reverse gap-3 border-t border-slate-100 bg-slate-50 px-4 py-4 sm:flex-row sm:justify-end sm:px-6 lg:px-8">
                  {readOnly ? (
                    <button
                      type="button"
                      onClick={handleEditClick}
                      className="w-full rounded-xl bg-[#1c394a] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#22475d] sm:w-auto sm:py-2.5"
                    >
                      Edit profile
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleCancelClick}
                        className="w-full rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100 sm:w-auto sm:py-2.5"
                      >
                        Cancel
                      </button>

                      <button
                        type="button"
                        onClick={handleSaveClick}
                        className="w-full rounded-xl bg-[#1c394a] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#22475d] sm:w-auto sm:py-2.5"
                      >
                        Save changes
                      </button>
                    </>
                  )}
                </div>
              </div>

            </main>
          </div>
        </div>

        {/* Mobile sidebar drawer */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setSidebarOpen(false)}
            />

            <div className="absolute left-0 top-0 h-[100dvh] w-[85%] max-w-[320px] bg-[#0b1220] shadow-xl border-r border-white/10 overflow-hidden flex flex-col">
              <div className="p-4 flex items-center justify-between border-b border-white/10">
                <p className="text-white text-sm font-semibold">Menu</p>
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="rounded-md px-2 py-1 text-sm text-white/80 hover:bg-white/10"
                >
                  ✕
                </button>
              </div>

              <div className="p-2 overflow-y-auto flex-1">
                <SideNavigation />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
